import React, { useState } from 'react';
import axios from 'axios';
import { Database, Loader2, Trash2 } from 'lucide-react';
import { API } from '../../config';

const SeedDataPanel = ({ token, onReload }) => {
  const [seedDays, setSeedDays] = useState(60);
  const [seeding, setSeeding] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [seedMessage, setSeedMessage] = useState('');

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const handleSeed = async () => {
    if (!window.confirm(`Seed 2 sample plants and ${seedDays} days of demo shift data?`)) return;
    setSeedMessage('');
    try {
      setSeeding(true);
      const res = await axios.post(`${API}/admin/seed-sample-data?days=${seedDays}`, {}, { headers });
      setSeedMessage(`✓ ${res.data.shifts_inserted} shifts inserted across ${res.data.plants_used} plants.`);
      if (onReload) await onReload();
    } catch (err) {
      setSeedMessage(`✗ ${err.response?.data?.detail || 'Failed to seed sample data'}`);
    } finally {
      setSeeding(false);
    }
  };

  const handleClearSample = async () => {
    if (!window.confirm('Remove the demo plants and ALL their shift data? This cannot be undone.')) return;
    setSeedMessage('');
    try {
      setClearing(true);
      const res = await axios.delete(`${API}/admin/sample-data`, { headers });
      setSeedMessage(`✓ Removed ${res.data.plants_removed} plant(s) and ${res.data.shifts_removed} shift(s).`);
      if (onReload) await onReload();
    } catch (err) {
      setSeedMessage(`✗ ${err.response?.data?.detail || 'Failed to clear sample data'}`);
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-[#156ACA]">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start space-x-3">
          <div className="bg-[#E8F2FC] p-2 rounded-lg"><Database className="h-5 w-5 text-[#156ACA]" /></div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Demo Data</h3>
            <p className="text-sm text-gray-600 mt-1">
              Populate the system with 2 sample plants and randomized shift data — useful for fresh installs or demos.
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-700">Days:</label>
          <input
            type="number" min="1" max="365" value={seedDays}
            onChange={(e) => setSeedDays(parseInt(e.target.value, 10) || 1)}
            className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#156ACA]"
            data-testid="seed-days-input"
          />
          <button
            onClick={handleSeed} disabled={seeding || clearing}
            className="bg-[#156ACA] hover:bg-[#0f56a3] text-white font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center"
            data-testid="seed-sample-data-btn"
          >
            {seeding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
            {seeding ? 'Seeding...' : 'Seed Sample Data'}
          </button>
          <button
            onClick={handleClearSample} disabled={seeding || clearing}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center"
            data-testid="clear-sample-data-btn"
          >
            {clearing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            {clearing ? 'Clearing...' : 'Clear Demo'}
          </button>
        </div>
      </div>
      {seedMessage ? (
        <div
          className={`mt-3 px-3 py-2 rounded-lg text-sm ${
            seedMessage.startsWith('✓')
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
          data-testid="seed-message"
        >
          {seedMessage}
        </div>
      ) : null}
    </div>
  );
};

export default SeedDataPanel;
